// src/context/NavigationContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { COLORS, TEXT_STYLES } from '@/theme';


// Check if code is running in browser environment
// @ts-ignore: Browser API check
const isBrowser = typeof window !== 'undefined';

// App sections
export type Section = 
  | 'dashboard' 
  | 'whatsapp'
  | 'messenger'
  | 'telegram'
  | 'instagram'
  | 'orders' 
  | 'clients' 
  | 'analytics' 
  //| 'broadcasts' 
  | 'settings'
  | 'profile'      
  | 'user-settings'
  | 'funnel'
  | 'marketing-expenses'; // Добавляем новый раздел маркетинговых расходов

// App pages
export type Page = 'home' | 'personal';

// Section groups
export type SectionGroup = 'chats' | 'management' | 'additional' | 'system' | 'user'; // Добавляем user группу

// Section metadata
export interface SectionInfo {
  id: Section;
  title: string;
  icon: string;
  group: SectionGroup;
  description?: string;
  path?: string; // Добавляем путь для каждой секции
}

// Group metadata
export interface GroupInfo {
  id: SectionGroup;
  title: string;
  order: number;
  description?: string;
}

// Navigation context type
interface NavigationContextType {
  currentPage: Page;
  currentSection: Section;
  navigateTo: (page: Page, section?: Section) => void;
  navigateToSection: (section: Section) => void; 
  setCurrentSection: (section: Section) => void;
  getSectionStyle: (sectionId: Section) => string;
  getGroupSections: (groupId: SectionGroup) => SectionInfo[];
  isGroupActive: (groupId: SectionGroup) => boolean;
  getGroupStyle: (groupId: SectionGroup) => string;
  resetState: () => void;
}

// Theme-aware section styles
export const sectionStyles = {
  active: `bg-${COLORS.primary}/10 text-${COLORS.primary}`,
  inactive: `text-${COLORS.text} hover:bg-${COLORS.background}`,
  icon: `w-5 h-5 mr-3`,
  text: TEXT_STYLES.body,
  wrapper: `w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200`,
  group: {
    title: `px-4 text-sm font-semibold text-${COLORS.secondary} uppercase tracking-wider`,
    wrapper: 'space-y-1',
    active: `border-l-2 border-${COLORS.primary}`,
    inactive: 'border-l-2 border-transparent'
  }
};

// Groups configuration - добавляем группу user
export const groups: Record<SectionGroup, GroupInfo> = {
  chats: {
    id: 'chats',
    title: 'Чаты',
    order: 1,
    description: 'Управление чатами в различных мессенджерах'
  },
  management: {
    id: 'management',
    title: 'Управление',
    order: 2,
    description: 'Основные инструменты управления'
  },
  additional: {
    id: 'additional',
    title: 'Дополнительно',
    order: 3,
    description: 'Дополнительные функции и аналитика'
  },
  system: {
    id: 'system',
    title: 'Система',
    order: 4,
    description: 'Системные настройки и конфигурация'
  },
  user: {
    id: 'user',
    title: 'Пользователь',
    order: 5,
    description: 'Профиль и настройки пользователя'
  }
};

// Section configuration with theme integration and paths - добавляем профиль и настройки пользователя
export const sections: Record<Section, SectionInfo> = {
  dashboard: {
    id: 'dashboard',
    title: 'Дашборд',
    icon: 'LayoutDashboard',
    group: 'management',
    description: 'Обзор основных показателей',
    path: '/personal/analytics/dashboard'
  },
  whatsapp: {
    id: 'whatsapp',
    title: 'WhatsApp',
    icon: 'WhatsApp',
    group: 'chats',
    description: 'Управление WhatsApp сообщениями',
    path: '/personal/platforms/whatsapp'
  },
  messenger: {
    id: 'messenger',
    title: 'Messenger',
    icon: 'Messenger',
    group: 'chats',
    description: 'Управление Facebook Messenger',
    path: '/personal/platforms/messenger'
  },
  telegram: {
    id: 'telegram',
    title: 'Telegram',
    icon: 'Telegram',
    group: 'chats',
    description: 'Управление Telegram сообщениями',
    path: '/personal/platforms/telegram'
  },
  instagram: {
    id: 'instagram',
    title: 'Instagram',
    icon: 'Instagram',
    group: 'chats',
    description: 'Управление Instagram Direct',
    path: '/personal/platforms/instagram'
  },
  orders: {
    id: 'orders',
    title: 'Заказы',
    icon: 'ShoppingCart',
    group: 'management',
    description: 'Управление заказами',
    path: '/personal/platforms/orders'
  },
  clients: {
    id: 'clients',
    title: 'Клиенты',
    icon: 'Users',
    group: 'management',
    description: 'База клиентов и история',
    path: '/personal/platforms/clients'
  },
  analytics: {
    id: 'analytics',
    title: 'Аналитика',
    icon: 'BarChart2',
    group: 'additional',
    description: 'Статистика и отчеты',
    path: '/personal/analytics/dashboard'
  },
  funnel: {
    id: 'funnel',
    title: 'Воронка продаж',
    icon: 'Funnel', // Используйте соответствующую иконку из lucide-react
    group: 'additional',
    description: 'Анализ стадий продаж',
    path: '/personal/analytics/funnel'
  },
  // Добавляем новый раздел маркетинговых расходов
  'marketing-expenses': {
    id: 'marketing-expenses',
    title: 'Маркетинговые расходы',
    icon: 'DollarSign', // Используйте подходящую иконку из lucide-react
    group: 'additional',
    description: 'Управление и анализ маркетинговых расходов',
    path: '/personal/analytics/marketing-expenses'
  },
// Временно отключено на первом этапе
// broadcasts: {
//   id: 'broadcasts',
//   title: 'Рассылки',
//   icon: 'Send',
//   group: 'management',
//   description: 'Управление рассылками',
//   path: '/personal/platforms/broadcasts'
// },
  settings: {
    id: 'settings',
    title: 'Настройки',
    icon: 'Settings',
    group: 'system',
    description: 'Системные настройки',
    path: '/personal/settings/application'
  },
  // Добавляем секции профиля и настроек пользователя
  profile: {
    id: 'profile',
    title: 'Профиль',
    icon: 'UserCircle',
    group: 'user',
    description: 'Управление профилем пользователя',
    path: '/personal/profile'
  },
  'user-settings': {
    id: 'user-settings',
    title: 'Настройки пользователя',
    icon: 'CreditCard',
    group: 'user',
    description: 'Настройки подписки и уведомлений',
    path: '/personal/user-settings'
  }
};

// Создаем хук для провайдера
export const NavigationProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  return (
    <NavigationProvider navigate={navigate} location={location}>
      {children}
    </NavigationProvider>
  );
};

// Create context with theme-aware default values
const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: React.ReactNode;
  navigate: (path: string) => void;
  location: { pathname: string };
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ 
  children,
  navigate,
  location 
}) => {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [currentSection, setCurrentSection] = useState<Section>('dashboard');

  const basename = '/rinnax-app';

  // Handle initial page state and section based on URL
  useEffect(() => {
    if (!isBrowser) return;
    
    const path = location.pathname;
    
    // Обработка путей профиля и настроек пользователя
    if (path.includes('/personal')) {
      setCurrentPage('personal');
      
      // Определяем текущий раздел по URL
      let foundSection = false;
      Object.entries(sections).forEach(([key, section]) => {
        if (section.path && path.includes(section.path)) {
          setCurrentSection(key as Section);
          foundSection = true;
        }
      });
      
      // Если раздел не найден, просто используем dashboard
      if (!foundSection) {
        setCurrentSection('dashboard');
      }
    } else {
      setCurrentPage('home');
    }
  }, [location.pathname]);

  // Обновленный навигатор использует React Router
  const navigateTo = (page: Page, section?: Section) => {
    setCurrentPage(page);
    
    if (page === 'home') {
      navigate(basename + '/');
    } else {
      // Если указана секция, переходим к ней
      if (section) {
        const sectionPath = sections[section].path || '/personal';
        navigate(basename + sectionPath);
        setCurrentSection(section);
      } else {
        // По умолчанию переходим к последней открытой секции
        const sectionPath = sections[currentSection].path || '/personal';
        navigate(basename + sectionPath);
      }
    }
  };
  
  // Новый метод для навигации к конкретному разделу
  const navigateToSection = (section: Section) => {
    const sectionPath = sections[section].path || '/personal';
    navigate(basename + sectionPath);
    setCurrentSection(section);
  };

  // Get section-specific styles
  const getSectionStyle = (sectionId: Section): string => {
    return currentSection === sectionId
      ? sectionStyles.active
      : sectionStyles.inactive;
  };

  // Get sections for a specific group
  const getGroupSections = (groupId: SectionGroup): SectionInfo[] => {
    return Object.values(sections)
      .filter(section => section.group === groupId)
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  // Check if group has active section
  const isGroupActive = (groupId: SectionGroup): boolean => {
    return sections[currentSection].group === groupId;
  };

  // Get group-specific styles
  const getGroupStyle = (groupId: SectionGroup): string => {
    return isGroupActive(groupId)
      ? sectionStyles.group.active
      : sectionStyles.group.inactive;
  };

  // Reset state function
  const resetState = () => {
    console.log('Resetting NavigationContext state');
    setCurrentPage('home');
    setCurrentSection('dashboard');
    navigate(basename + '/');
  };

  // Слушатель события выхода из системы
  React.useEffect(() => {
    if (!isBrowser) return;
    
    const handleLogout = () => {
      console.log('NavigationProvider received logout event');
      resetState();
    };
    
    // @ts-ignore: Browser API
    window.addEventListener('auth:logout:success', handleLogout);
    
    return () => {
      // @ts-ignore: Browser API
      window.removeEventListener('auth:logout:success', handleLogout);
    };
  }, []);

  const contextValue: NavigationContextType = {
    currentPage,
    currentSection,
    navigateTo,
    navigateToSection,
    setCurrentSection,
    getSectionStyle,
    getGroupSections,
    isGroupActive,
    getGroupStyle,
    resetState
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

// Custom hook with theme integration
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};